import { Table, Select, Input, Button, Space, Tag, Alert } from 'antd';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useProducts } from '../hooks/useProducts';
import { fetchProductCategories, fetchProductTags, fetchProductWarehouseLocations } from '../api/products.api';
import { useAuth } from '../context/AuthContext';
import { useStores } from '../hooks/useStores';

const baseFilters = { page: 1, pageSize: 20 };

function getStorePrefix(storeName: string): string {
  return storeName.replace(/\s*-\s*fulfillment\s*$/i, '').trim();
}

export default function ProductsReport() {
  const { user } = useAuth();
  const stores = useStores();

  const { data: allCategories = [] } = useQuery({
    queryKey: ['product-categories'],
    queryFn: fetchProductCategories,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['product-tags'],
    queryFn: fetchProductTags,
  });

  const { data: allWarehouseLocations = [] } = useQuery({
    queryKey: ['product-warehouse-locations'],
    queryFn: fetchProductWarehouseLocations,
  });

  // Derive which categories are available based on role + assigned stores
  const availableCategories: { categoryId: number; name: string }[] = (() => {
    if (user?.type === 'superadmin') return allCategories;

    const assignedStoreIds = (user?.assignedStores ?? []).map(String);

    if (user?.type === 'admin' && assignedStoreIds.length === 0) return allCategories;

    if (assignedStoreIds.length === 0) return allCategories;

    const assignedStoreNames = stores
      .filter(s => assignedStoreIds.includes(String(s.storeId)))
      .map(s => s.storeName);
      
    const prefixes = assignedStoreNames.map(getStorePrefix);
    console.log(prefixes);
    return allCategories.filter((cat: any) =>
      prefixes.some(prefix =>
        cat.name?.toLowerCase().startsWith(prefix.toLowerCase())
      )
    );
  })();

  const [filters, setFilters] = useState<any>(baseFilters);
  const { data, isLoading, isError } = useProducts(filters);

  const [local, setLocal] = useState<any>({
    sku: '',
    name: '',
    active: undefined,
    warehouseLocation: '',
    categoryId: undefined,
    tagId: undefined,
  });

  function applyFilters() {
    const mapped: any = { ...baseFilters, page: 1 };
    if (local.sku) mapped.sku = local.sku;
    if (local.name) mapped.name = local.name;
    if (local.active !== undefined) mapped.active = local.active;
    if (local.warehouseLocation) mapped.warehouseLocation = local.warehouseLocation;
    if (local.categoryId !== undefined) mapped.categoryId = local.categoryId;
    if (local.tagId !== undefined) mapped.tagId = local.tagId;
    setFilters(mapped);
  }

  function clearFilters() {
    setLocal({ sku: '', name: '', active: undefined, warehouseLocation: '', categoryId: undefined, tagId: undefined });
    setFilters(baseFilters);
  }

  return (
    <div style={{ display: 'flex', gap: 16 }}>
      <div style={{ flex: 1 }}>
        {isError && (
          <Alert
            type="error"
            message="Failed to load products. The server may be unavailable (503). Please try again later."
            style={{ marginBottom: 12 }}
          />
        )}
        <div style={{ textAlign: 'right', marginBottom: 10 }}>
          <strong>Total Records:</strong> {data?.total || 0}
        </div>
        <Table
          loading={isLoading}
          rowKey="productId"
          dataSource={data?.products}
          scroll={{ x: true }}
          pagination={{
            current: filters.page,
            pageSize: filters.pageSize,
            total: data?.total,
          }}
          onChange={(pagination: any) => {
            setFilters({ ...filters, page: pagination.current, pageSize: pagination.pageSize });
          }}
          columns={[
            { title: 'Product ID', dataIndex: 'productId', width: 100 },
            {
              title: 'Thumbnail',
              dataIndex: 'thumbnailURL',
              width: 80,
              render: (url) =>
                url ? <img src={url} alt="thumb" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} /> : '-',
            },
            { title: 'SKU', dataIndex: 'sku', sorter: false },
            { title: 'Name', dataIndex: 'name', sorter: false },
            {
              title: 'Active',
              dataIndex: 'active',
              width: 80,
              render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
            },
            {
              title: 'Price',
              dataIndex: 'price',
              width: 90,
              render: (v) => v != null ? `$${Number(v).toFixed(2)}` : '-',
            },
            {
              title: 'Cost',
              dataIndex: 'defaultCost',
              width: 90,
              render: (v) => v != null ? `$${Number(v).toFixed(2)}` : '-',
            },
            { title: 'Category', dataIndex: 'categoryName', render: (v) => v || '-' },
            { title: 'Location', dataIndex: 'warehouseLocation', width: 150, render: (v) => v || '-' },
            {
              title: 'Tags',
              dataIndex: 'tags',
              render: (tags: any[]) =>
                Array.isArray(tags) && tags.length > 0
                  ? tags.map(t => <Tag key={t.tagId} style={{ marginBottom: 2 }}>{t.name}</Tag>)
                  : '-',
            },
            { title: 'UPC', dataIndex: 'upc', render: (v) => v || '-' },
            {
              title: 'Modify Date',
              dataIndex: 'modifyDate',
              render: (d) => d ? new Date(d).toLocaleDateString('en-US') : '-',
            },
          ]}
        />
      </div>

      <div style={{ width: 220, minWidth: 220, flexShrink: 0, borderLeft: '1px solid #eee', paddingLeft: 16 }}>
        <h3>Filters</h3>

        <Space orientation="vertical" style={{ width: '100%' }}>
          <label>SKU</label>
          <Input
            placeholder="SKU"
            value={local.sku}
            onChange={e => setLocal({ ...local, sku: e.target.value })}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Name</label>
          <Input
            placeholder="Product name"
            value={local.name}
            onChange={e => setLocal({ ...local, name: e.target.value })}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Active</label>
          <Select
            placeholder="All"
            allowClear
            style={{ width: '100%' }}
            value={local.active}
            onChange={val => setLocal({ ...local, active: val })}
            options={[
              { label: 'Active', value: 'true' },
              { label: 'Inactive', value: 'false' },
            ]}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Warehouse Location</label>
          <Select
            placeholder="All"
            allowClear
            showSearch
            style={{ width: '100%' }}
            value={local.warehouseLocation || undefined}
            onChange={val => setLocal({ ...local, warehouseLocation: val ?? '' })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={(allWarehouseLocations as string[]).map(loc => ({
              label: loc,
              value: loc,
            }))}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Category</label>
          <Select
            placeholder="All"
            allowClear
            showSearch
            style={{ width: '100%' }}
            value={local.categoryId}
            onChange={val => setLocal({ ...local, categoryId: val })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={availableCategories.map((c: any) => ({
              label: c.name,
              value: c.categoryId,
            }))}
          />
        </Space>

        <Space orientation="vertical" style={{ width: '100%', marginTop: 12 }}>
          <label>Tag</label>
          <Select
            placeholder="All"
            allowClear
            showSearch
            style={{ width: '100%' }}
            value={local.tagId}
            onChange={val => setLocal({ ...local, tagId: val })}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={(allTags as any[]).map((t: any) => ({
              label: t.name,
              value: t.tagId,
            }))}
          />
        </Space>

        <Space style={{ marginTop: 16 }}>
          <Button type="primary" onClick={applyFilters}>Apply</Button>
          <Button onClick={clearFilters}>Clear</Button>
        </Space>
      </div>
    </div>
  );
}
